import React from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import {
  GLOSSARY_CATEGORIES,
  findGlossaryEntry,
  findGlossaryEntryBySlug,
  glossaryRoute,
  type GlossaryEntry,
} from "@features/investments/glossary/registry";

type GlossaryTermVariant = "inline" | "metric" | "table-header" | "kpi" | "kv";

type GlossaryTermProps = {
  label: string;
  term?: string;
  slug?: string;
  className?: string;
  variant?: GlossaryTermVariant;
};

const categoryLabelByKey = new Map(GLOSSARY_CATEGORIES.map((category) => [category.key, category.label]));

export function GlossaryTerm({
  label,
  term,
  slug,
  className = "",
  variant = "inline",
}: GlossaryTermProps): JSX.Element {
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);
  const entry = React.useMemo(
    () => resolveGlossaryEntry({ label, term, slug }),
    [label, slug, term],
  );
  const touchMode = useTouchPreviewMode();
  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [previewPosition, setPreviewPosition] = React.useState<{
    left: number;
    top: number;
    width: number;
    placement: "top" | "bottom";
  } | null>(null);

  const linkClassName = [
    "glossary-term-link",
    `glossary-term-link-${variant}`,
    className,
  ].filter(Boolean).join(" ");

  const updatePreviewPosition = React.useCallback(() => {
    if (typeof window === "undefined" || !linkRef.current) {
      return;
    }

    const rect = linkRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const preferredWidth = 288;
    const width = Math.min(preferredWidth, window.innerWidth - viewportPadding * 2);
    const centeredLeft = rect.left + rect.width / 2;
    const minimumLeft = viewportPadding + width / 2;
    const maximumLeft = window.innerWidth - viewportPadding - width / 2;
    const left = Math.min(Math.max(centeredLeft, minimumLeft), Math.max(minimumLeft, maximumLeft));
    const placeAbove = rect.top >= 112;

    setPreviewPosition({
      left,
      top: placeAbove ? rect.top - 10 : rect.bottom + 10,
      width,
      placement: placeAbove ? "top" : "bottom",
    });
  }, []);

  React.useEffect(() => {
    if (!previewVisible || touchMode) {
      return undefined;
    }

    updatePreviewPosition();

    function handleViewportChange(): void {
      updatePreviewPosition();
    }

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [previewVisible, touchMode, updatePreviewPosition]);

  React.useEffect(() => {
    if (!sheetOpen) {
      return undefined;
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setSheetOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [sheetOpen]);

  React.useEffect(() => {
    if (touchMode) {
      setPreviewVisible(false);
      return;
    }
    setSheetOpen(false);
  }, [touchMode]);

  if (!entry) {
    return <span className={className}>{label}</span>;
  }

  return (
    <>
      <Link
        ref={linkRef}
        className={linkClassName}
        to={glossaryRoute(entry.slug)}
        onClick={(event) => {
          if (!touchMode) {
            return;
          }
          event.preventDefault();
          setSheetOpen(true);
        }}
        onMouseEnter={() => {
          if (!touchMode) {
            setPreviewVisible(true);
          }
        }}
        onMouseLeave={() => {
          if (!touchMode) {
            setPreviewVisible(false);
          }
        }}
        onFocus={() => {
          if (!touchMode) {
            setPreviewVisible(true);
          }
        }}
        onBlur={() => {
          setPreviewVisible(false);
        }}
      >
        {label}
      </Link>

      {previewVisible && previewPosition && typeof document !== "undefined" && createPortal(
        <GlossaryPreviewTooltip entry={entry} position={previewPosition} />,
        document.body,
      )}

      {sheetOpen && typeof document !== "undefined" && createPortal(
        <GlossaryPreviewSheet entry={entry} onClose={() => setSheetOpen(false)} />,
        document.body,
      )}
    </>
  );
}

function GlossaryPreviewTooltip({
  entry,
  position,
}: {
  entry: GlossaryEntry;
  position: {
    left: number;
    top: number;
    width: number;
    placement: "top" | "bottom";
  };
}): JSX.Element {
  return (
    <div
      className="glossary-term-preview glossary-term-preview-tooltip"
      role="tooltip"
      data-placement={position.placement}
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
        transform: position.placement === "top" ? "translate(-50%, -100%)" : "translate(-50%, 0)",
      }}
    >
      <GlossaryPreviewBody entry={entry} />
      <div className="glossary-term-preview-hint">Click to open glossary</div>
    </div>
  );
}

function GlossaryPreviewSheet({
  entry,
  onClose,
}: {
  entry: GlossaryEntry;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="glossary-preview-sheet-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="glossary-preview-sheet" role="dialog" aria-modal="true" aria-label={`${entry.label} glossary preview`}>
        <GlossaryPreviewBody entry={entry} />
        <div className="glossary-preview-sheet-actions">
          <button type="button" className="glossary-preview-sheet-secondary" onClick={onClose}>
            Close
          </button>
          <Link className="glossary-preview-sheet-primary" to={glossaryRoute(entry.slug)} onClick={onClose}>
            Open glossary
          </Link>
        </div>
      </div>
    </div>
  );
}

function GlossaryPreviewBody({ entry }: { entry: GlossaryEntry }): JSX.Element {
  return (
    <>
      <div className="glossary-term-preview-meta">{categoryLabelByKey.get(entry.category) ?? entry.category}</div>
      <div className="glossary-term-preview-title">{entry.label}</div>
      <p className="glossary-term-preview-copy">{entry.shortDefinition}</p>
      {entry.formula && <div className="glossary-term-preview-formula">{entry.formula}</div>}
    </>
  );
}

function resolveGlossaryEntry({
  label,
  term,
  slug,
}: {
  label: string;
  term?: string;
  slug?: string;
}): GlossaryEntry | null {
  if (slug) {
    return findGlossaryEntryBySlug(slug);
  }
  return findGlossaryEntry(term ?? label);
}

function useTouchPreviewMode(): boolean {
  const [touchMode, setTouchMode] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    const update = (): void => setTouchMode(mediaQuery.matches);
    update();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    }

    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  return touchMode;
}
